import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Pencil, Trash, Plus, X, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATIONS = [
  { v: "taquero1", l: "Pastor (Taquero 1)" },
  { v: "taquero2", l: "Plancha (Taquero 2)" },
  { v: "taquero3", l: "Barra/Bebidas (Taquero 3)" },
];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", price: 0, category: "Tacos", kitchen_station: "taquero1", image_url: "", top_seller: false, active: true });

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm({ name: "", price: 0, category: "Tacos", kitchen_station: "taquero1", image_url: "", top_seller: false, active: true }); };
  const startEdit = (p) => { setEditing(p); setForm({ ...p, image_url: p.image_url || "" }); };
  const close = () => setEditing(null);

  const save = async () => {
    try {
      const body = { ...form, price: Number(form.price), image_url: form.image_url || null };
      if (editing === "new") {
        await api.post("/products", body);
        toast.success("Producto creado");
      } else {
        await api.patch(`/products/${editing.id}`, body);
        toast.success("Producto actualizado");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Menú</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Productos y precios</h1>
        </div>
        <button onClick={startNew} className="btn-brand h-12 px-5 inline-flex items-center gap-2" data-testid="products-new">
          <Plus size={20} weight="bold" /> Nuevo Producto
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid={`products-card-${p.id}`}>
            <div className="aspect-[4/3] bg-zinc-100 relative">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-300 font-display text-4xl">{p.category[0]}</div>
              )}
              {p.top_seller && <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Star size={10} weight="fill"/> Top</div>}
              {!p.active && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-bold uppercase text-sm tracking-wider">Inactivo</div>}
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">{p.category} · {p.kitchen_station}</div>
              <div className="flex items-baseline justify-between gap-2 mt-1">
                <div className="font-bold text-base leading-tight">{p.name}</div>
                <div className="font-display font-black text-brand text-xl">${p.price}</div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(p)} className="flex-1 h-9 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-sm font-semibold inline-flex items-center justify-center gap-1" data-testid={`products-edit-${p.id}`}><Pencil size={14} weight="bold" /> Editar</button>
                <button onClick={() => remove(p)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 inline-flex items-center justify-center" data-testid={`products-delete-${p.id}`}><Trash size={14} weight="bold" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[92vh] overflow-y-auto pos-scroll">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-xl font-bold">{editing === "new" ? "Nuevo producto" : "Editar producto"}</div>
              <button onClick={close} className="text-zinc-400 hover:text-red-500"><X size={22} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Nombre</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="products-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Precio MXN</label>
                  <input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="products-form-price" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Categoría</label>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="products-form-category" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Estación de cocina</label>
                <select value={form.kitchen_station} onChange={(e) => setForm({ ...form, kitchen_station: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="products-form-station">
                  {STATIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">URL imagen (opcional)</label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none text-sm" data-testid="products-form-image" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.top_seller} onChange={(e) => setForm({ ...form, top_seller: e.target.checked })} />
                  <span className="text-sm font-semibold">Top vendido</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <span className="text-sm font-semibold">Activo</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={close} className="flex-1 h-12 rounded-xl bg-zinc-100 font-semibold">Cancelar</button>
              <button onClick={save} className="flex-1 h-12 rounded-xl btn-brand font-semibold" data-testid="products-form-save">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
